import { useEffect, useRef } from 'react';

// 이 거리(px) 이상 끌면 '드래그'로 간주 — 미만이면 일반 탭(클릭)으로 통과.
const DRAG_THRESHOLD_PX = 6;

/**
 * 가로 스크롤 컨테이너를 마우스/펜으로 끌어 넘길 수 있게 한다.
 *
 * - 터치는 브라우저 네이티브 관성 스크롤에 맡긴다(pointerType === 'touch' 무시).
 * - 끌어서 넘긴 직후의 click 1회를 캡처 단계에서 무력화해, 드래그 끝에서
 *   칩이 선택돼 버리는 현상을 막는다.
 *
 * 반환한 ref를 overflow-x-auto 컨테이너에 그대로 붙이면 된다.
 */
export function useDragScroll<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    if (!ref.current) return;
    // 선언 타입을 non-null로 고정 — 중첩 핸들러(클로저)에서 좁힘이 유지되도록.
    const el: T = ref.current;

    let activePointer: number | null = null;
    let startX = 0;
    let startScroll = 0;
    let moved = false;

    function onPointerDown(e: PointerEvent) {
      if (e.pointerType === 'touch') return; // 터치는 네이티브 스크롤
      activePointer = e.pointerId;
      startX = e.clientX;
      startScroll = el.scrollLeft;
      moved = false;
    }

    function onPointerMove(e: PointerEvent) {
      if (activePointer !== e.pointerId) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) < DRAG_THRESHOLD_PX) return;
      if (!moved) {
        moved = true;
        try {
          el.setPointerCapture(activePointer);
        } catch {
          // 캡처 실패해도 스크롤 자체는 동작
        }
        el.style.cursor = 'grabbing';
        el.style.userSelect = 'none';
      }
      el.scrollLeft = startScroll - dx;
      e.preventDefault();
    }

    function endDrag(e: PointerEvent) {
      if (activePointer !== e.pointerId) return;
      if (moved) {
        el.style.cursor = '';
        el.style.userSelect = '';
        // 드래그 직후의 click 1회만 삼킨다(칩 선택 방지). 클릭이 안 오면 다음 틱에 제거.
        const swallowClick = (ev: Event) => {
          ev.stopPropagation();
          ev.preventDefault();
          el.removeEventListener('click', swallowClick, true);
        };
        el.addEventListener('click', swallowClick, { capture: true });
        setTimeout(() => el.removeEventListener('click', swallowClick, true), 350);
      }
      try {
        el.releasePointerCapture(e.pointerId);
      } catch {
        // 이미 해제됐을 수 있음
      }
      activePointer = null;
    }

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', endDrag);
    el.addEventListener('pointercancel', endDrag);
    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', endDrag);
      el.removeEventListener('pointercancel', endDrag);
    };
  }, []);

  return ref;
}
